import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import { seoArticles, type ArticleSection, type SeoArticle } from '@/mocks/superbuy';

function ArticleNotFound() {
  return (
    <main>
      <Navbar />
      <div className="pt-32 pb-24 bg-background-50 text-center">
        <div className="max-w-[600px] mx-auto px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-file-search-line text-primary-500 text-2xl"></i>
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-8">The article you are looking for does not exist or may have been moved to a different location.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300"
          >
            Back to Homepage
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </Link>
        </div>
      </div>
      <FooterSection />
    </main>
  );
}

function renderSection(section: ArticleSection) {
  switch (section.type) {
    case 'h2':
      return (
        <section className="mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {section.title}
          </h2>
        </section>
      );

    case 'paragraphs':
      return (
        <div className="mb-10">
          {section.content?.map((p, i) => (
            <p
              key={i}
              className="text-foreground-700 leading-relaxed mb-4"
              dangerouslySetInnerHTML={{ __html: p }}
            />
          ))}
        </div>
      );

    case 'ordered_list':
      return (
        <div className="mb-10">
          <ol className="list-decimal list-inside space-y-3 text-foreground-700 leading-relaxed">
            {section.items?.map((item, i) => (
              <li key={i}>
                <strong>{item.title}</strong>{item.desc ? ` — ${item.desc}` : ''}
              </li>
            ))}
          </ol>
        </div>
      );

    case 'card_grid':
      return (
        <div className="mb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.items?.map((item) => (
              <div key={item.title} className="bg-background-100 rounded-xl p-5 flex items-start gap-3">
                {item.icon && (
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-primary-600 text-sm`}></i>
                  </div>
                )}
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">{item.title}</h3>
                  {item.desc && <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'category_list':
      return (
        <div className="mb-10">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {section.items?.map((item) => (
              <div key={item.title} className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2.5">
                {item.icon && (
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-primary-400 text-xs`}></i>
                  </div>
                )}
                <span className="text-sm text-foreground-700">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case 'tip_box':
      return (
        <div className="mb-10">
          <div className="bg-accent-50 border border-accent-200/50 rounded-2xl p-6">
            {section.title && (
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                {section.tip_icon && (
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${section.tip_icon} text-accent-500`}></i>
                  </div>
                )}
                {section.title}
              </h3>
            )}
            <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
              {section.items?.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-accent-500 font-bold mt-0.5">{i + 1}.</span>
                  <span>
                    <strong>{item.title}</strong>{item.desc ? ` — ${item.desc}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );

    case 'comparison_table':
      return (
        <div className="mb-10">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b-2 border-background-300">
                  <th className="text-left py-3 px-4 text-foreground-950 font-semibold">Feature</th>
                  <th className="text-left py-3 px-4 text-foreground-500">Manual Search</th>
                  <th className="text-left py-3 px-4 text-primary-600 font-semibold">Superbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {section.rows?.map((row) => (
                  <tr key={row.label} className="border-b border-background-200">
                    <td className="py-3 px-4 text-foreground-700 font-medium">{row.label}</td>
                    <td className="py-3 px-4 text-foreground-500">{row.col1}</td>
                    <td className="py-3 px-4 text-primary-600 font-medium">{row.col2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case 'faq_list':
      return (
        <div className="mb-10">
          <div className="space-y-4">
            {section.qa_pairs?.map((faq, i) => (
              <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-950 font-semibold text-sm list-none">
                  {faq.question}
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-add-line group-open:hidden text-foreground-500"></i>
                    <i className="ri-subtract-line hidden group-open:block text-foreground-500"></i>
                  </div>
                </summary>
                <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      );

    case 'image':
      return (
        <figure className="mb-10 rounded-2xl overflow-hidden">
          <img
            src={section.image}
            alt={section.caption || ''}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
          {section.caption && (
            <figcaption className="text-xs text-foreground-500 mt-2 text-center italic">
              {section.caption}
            </figcaption>
          )}
        </figure>
      );

    case 'cta':
      return (
        <section className="mb-10">
          {section.content?.map((p, i) => (
            <p key={i} className="text-foreground-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
          ))}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-7 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center gap-2"
            >
              Get Superbuy Sheet Now
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
            <Link
              to="/"
              className="border-2 border-foreground-300 hover:border-foreground-800 text-foreground-700 px-7 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-300"
            >
              Back to Homepage
            </Link>
          </div>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article: SeoArticle | undefined = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return <ArticleNotFound />;
  }

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuysheet.lat';
  const pageUrl = `${siteUrl}/article/${article.slug}`;
  const faqPairs = article.sections.find((s) => s.type === 'faq_list')?.qa_pairs || [];

  return (
    <main>
      <Navbar />

      <article className="pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li className="flex items-center gap-2">
                <span>/</span>
                <span className="text-foreground-700">{article.tag}</span>
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              {article.tag}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 text-sm font-bold">SS</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Superbuy Sheet Team</p>
                <p className="text-xs text-foreground-500">Updated {article.date} · {article.readTime}</p>
              </div>
            </div>
          </header>

          <figure className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover"
            />
          </figure>

          <div className="prose-custom">
            {article.sections.map((section, i) => (
              <div key={i}>
                {renderSection(section)}
              </div>
            ))}
          </div>
        </div>
      </article>

      <FooterSection />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.tag,
                item: pageUrl,
              },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.heroImage,
            author: {
              '@type': 'Organization',
              name: 'Superbuy Sheet Team',
              url: siteUrl,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Superbuy Spreadsheet Hub',
              url: siteUrl,
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04e8ea78b88107.png',
              },
            },
            datePublished: '2026-07-01',
            dateModified: '2026-07-13',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': pageUrl,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqPairs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
              },
            })),
          }),
        }}
      />
    </main>
  );
}